import { Box } from '@zoopla-eng/zds/box';
import { Card } from '@zoopla-eng/zds/cards/property/house-prices';
import { Divider } from '@zoopla-eng/zds/divider';
import { InfoPanel } from '@zoopla-eng/zds/info-panel';
import { Pagination } from '@zoopla-eng/zds/pagination';
import { fontGroups, queries } from '@zoopla-eng/zds/themes';
import {
    ADBRIDG_CLASS,
    AdvertSlotDfp,
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    coerceDfpAdTargetingObject,
    getPageSectionForAdUnit
} from '@zoopla/adverts/dfp';
import { graphQLWrapper } from '@zoopla/apollo';
import { Layout } from '@zoopla/house-prices/layout';
import { HousePricesSearch } from '@zoopla/house-prices/search';
import { Head } from '@zoopla/layout/head';
import { useSchema } from '@zoopla/layout/schema';
import { TrackPropertyProvider } from '@zoopla/property-details/track-property';
import { formatter as currencyFormatter } from '@zoopla/shared/currency';
import { trackEvent, triggerTracking, usePageTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Filters from './components/Filters';
import type { Option } from './components/Filters/options';
import {
    lastSoldOptions,
    propertyTypeOptions,
    sortOptions
} from './components/Filters/options';
import { InlineAdSlot } from './components/InlineAdSlot';
import { InstantValuationCTA } from './components/InstantValuationCTA/InstantValuationCTA';
import { PropertyCountLabel } from './components/PropertyCountLabel';
import { SeoBlurb } from './components/SeoBlurb';
import {
    PropertiesSearchConnection,
    PropertiesSearchEdge,
    Property
} from './graphql.generated';
import {
    buildQueryUrl,
    getPricesHead,
    getSortValue,
    mapAttributes,
    PAGE_SIZE
} from './helpers';
import {
    disclaimerTextStyle,
    dividerWrapperStyle,
    listStyle,
    pageHeadingStyle,
    sectionStyle
} from './HousePricesResults.styles.css';
import type {
    BreadcrumbsItemsProps,
    HousePricesResultsProps
} from './HousePricesResults.types';
import TrackPropertyWrapper from './TrackPropertyWrapper';

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

export const HousePricesResultsPage = ({
    data,
    noindex,
    path,
    extraTaxonomy,
    pageNumber
}: HousePricesResultsProps) => {
    const { asPath, push, query, route } = useRouter();

    const {
        area,
        yearAllActivity,
        yearDetachedActivity,
        yearFlatActivity,
        yearTerracedActivity,
        yearSemiActivity,
        propertiesSearch,
        breadcrumbs
    } = data;

    let properties: PropertiesSearchEdge[] = [];

    let areaName;

    if (propertiesSearch?.__typename === 'PropertiesSearchConnection') {
        const { metaData, edges } = propertiesSearch ?? {};

        extraTaxonomy = {
            activity: 'house_prices_search',
            section: 'house-prices',
            page: '/house-prices/results/',
            location: metaData?.input?.geoString || metaData?.geoDescription,
            countyAreaName: metaData?.geoCountyAreaName,
            regionName: metaData?.geoRegionName,
            outcodes: metaData?.geoOutcodes,
            outcode: metaData?.geoOutcode || '',
            fullStreetName: metaData?.geoDescription,
            alias: metaData?.geoAlias,
            postalArea: metaData?.geoPostalArea,
            postTownName: metaData?.geoPostTownName,
            incode: metaData?.geoIncode ?? null,
            resultsSort:
                (query?.sortOrder as string)?.toLowerCase() || 'address'
        };

        noindex = (edges?.length ?? 0) < 2;

        properties = propertiesSearch?.edges as PropertiesSearchEdge[];
        areaName =
            propertiesSearch?.metaData?.geoDescription ||
            propertiesSearch?.metaData?.geoName;
    }

    usePageTracking('/house-prices/', {
        ...extraTaxonomy
    });

    const pageNumberRegex = /(\?|&)pn=[0-9]+/;
    const url = asPath.replace(pageNumberRegex, '');
    const currentPage = query.pn ? parseInt(query.pn as string, 10) : 1;
    const pageNumberToUrl = (pageNumber: number) => {
        const queryCharacter = url.charAt(url.length - 1) === '/' ? '?' : '&';
        return `${url}${
            pageNumber === 1 ? '' : `${queryCharacter}pn=${pageNumber}`
        }`;
    };

    const totalResults =
        (propertiesSearch as PropertiesSearchConnection)?.totalResults || 0;

    const hasPropertyResults =
        properties !== null && properties !== undefined && totalResults > 0;

    const averagePricePaid = yearAllActivity?.data?.averagePricePaid || 0;

    const areaShortName = data?.area?.shortName || areaName;
    const schema = useSchema(breadcrumbs as BreadcrumbsItemsProps, path);
    const head = getPricesHead({
        areaName,
        averagePricePaid,
        noindex,
        path,
        schema
    });
    const pageHeading = area ? `House prices in ${areaName}` : 'House prices';

    const [propertyBeingTracked, setPropertyBeingTracked] = useState<
        Property | undefined
    >(undefined);

    // Figure out if any existings filters are applied:
    const [selectedPropertyTypeOption, setSelectedPropertyTypeOption] =
        useState<Option>(propertyTypeOptions[0]);

    const [selectedLastSoldOption, setSelectedLastSoldOption] =
        useState<Option>(lastSoldOptions[0]);
    const [selectedSortOption, setSelectedSortOptionChange] = useState<Option>(
        sortOptions[0]
    );

    useEffect(() => {
        const existingSortOrder = {
            address: 'address',
            last_sale_date: 'sold',
            last_sale_price: 'price-paid'
        }[query?.sortOrder as string];

        const existingSortDirection = query?.sortDirection;

        const existingSortOptionId =
            existingSortOrder &&
            existingSortDirection &&
            `${existingSortOrder}-${existingSortDirection}`;

        const existingSortOption = sortOptions.find(
            ({ id }) => id === existingSortOptionId
        );

        const existingPropertyTypeOption =
            propertyTypeOptions.find(
                ({ id }) => id === query?.propertyTypeCode
            ) || propertyTypeOptions[0];

        const existingLastSoldOption =
            lastSoldOptions.find(
                ({ numMonths }) => numMonths?.toString() === query?.numMonths
            ) || lastSoldOptions[0];

        setSelectedPropertyTypeOption(existingPropertyTypeOption);
        setSelectedLastSoldOption(existingLastSoldOption);
        setSelectedSortOptionChange(existingSortOption ?? sortOptions[2]);
    }, [
        query?.numMonths,
        query?.propertyTypeCode,
        query?.sortOrder,
        query?.sortDirection
    ]);

    const handleSortOptionChange = (option: Option) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'market_activity',
            eventAction: 'dropdown_sort',
            eventLabel: option.text.toLowerCase()
        });
        // Get the sort values based on the option id
        const sortValue = getSortValue(option.id);

        // Apply them to relevant properties
        const newQueryParams = {
            ...query,
            sortOrder: sortValue.sortOrder,
            sortDirection: sortValue.sortDirection
        };

        const url = buildQueryUrl(asPath, newQueryParams);
        push(url, undefined, { scroll: false });
        setSelectedSortOptionChange(option);
    };

    const buildEstimateString = (lowerVal?: number, upperVal?: number) => {
        if (!!lowerVal && !!upperVal) {
            return `${currencyFormatter.format(
                lowerVal
            )} - ${currencyFormatter.format(upperVal)}`;
        } else {
            return '-';
        }
    };

    const handleLastSoldOptionChange = (option: Option) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'market_activity',
            eventAction: 'dropdown_historical_timeframe',
            eventLabel: option.numMonths?.toString()
        });
        const newQueryParams = {
            ...query
        };

        if (option.numMonths) {
            newQueryParams.numMonths = option?.numMonths.toString();
        } else {
            // Remove param to query for all-time
            delete newQueryParams.numMonths;
        }

        const url = buildQueryUrl(asPath, newQueryParams);
        push(url, undefined, { scroll: false });
        setSelectedLastSoldOption(option);
    };

    const handlePropertyTypeOptionsChange = (option: Option) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'market_activity',
            eventAction: 'dropdown_property_type',
            eventLabel: option.text.toLowerCase()
        });

        const newQueryParams = {
            ...query
        };

        if (option.id !== 'any') {
            newQueryParams.propertyTypeCode = option.id;
        } else {
            // Remove param to query for 'any' property code
            delete newQueryParams.propertyTypeCode;
        }
        const url = buildQueryUrl(asPath, newQueryParams);
        push(url, undefined, { scroll: false });
        setSelectedPropertyTypeOption(option);
    };

    const slug = propertiesSearch?.metaData?.input?.geoIdentifier || '';

    return (
        <>
            <Head {...head} />
            <Layout
                breadcrumbs={
                    data?.breadcrumbs?.filter(notEmpty).map((bc) => ({
                        ariaLabel: bc.text ?? '',
                        id: bc.id ?? '',
                        href: bc.href ?? '',
                        text: bc.text ?? ''
                    })) ?? [
                        {
                            ariaLabel: 'Zoopla',
                            id: 'zoopla',
                            href: '/',
                            text: 'Zoopla'
                        },
                        {
                            ariaLabel: 'House prices',
                            id: 'house-prices',
                            href: '/house-prices/',
                            text: 'House prices'
                        }
                    ]
                }
                dfpAdTargeting={coerceDfpAdTargetingObject(extraTaxonomy)}
                pageNumber={pageNumber}
                isPending={false}
            >
                <Box
                    fw
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                >
                    <Box
                        width="100%"
                        pb="S500"
                        pt={0}
                        bpS200={{ pt: 'S400' }}
                        bpM100={{ pt: 0 }}
                    >
                        <HousePricesSearch isLandingPage={false} />
                    </Box>
                    <div className={sectionStyle}>
                        <h1
                            className={`${pageHeadingStyle} ${fontGroups.plate}`}
                        >
                            {pageHeading}
                        </h1>
                        {yearAllActivity?.data?.propertiesSold &&
                        yearAllActivity?.data?.propertiesSold > 0 ? (
                            <SeoBlurb
                                areaName={areaShortName}
                                seoBlurbStats={{
                                    all: yearAllActivity,
                                    detached: yearDetachedActivity,
                                    flat: yearFlatActivity,
                                    terraced: yearTerracedActivity,
                                    semi: yearSemiActivity
                                }}
                            />
                        ) : null}
                        {areaName && (
                            <InstantValuationCTA
                                areaName={areaName}
                                slug={slug}
                            />
                        )}
                        <div className={dividerWrapperStyle}>
                            <Divider direction="horizontal" />
                        </div>
                        <Filters
                            handleLastSoldOptionChange={
                                handleLastSoldOptionChange
                            }
                            selectedLastSoldOption={selectedLastSoldOption}
                            handlePropertyTypeOptionsChange={
                                handlePropertyTypeOptionsChange
                            }
                            selectedPropertyTypeOption={
                                selectedPropertyTypeOption
                            }
                            handleSortOptionChange={handleSortOptionChange}
                            selectedSortOption={selectedSortOption}
                        />
                        {hasPropertyResults ? (
                            <>
                                <PropertyCountLabel
                                    totalResults={totalResults}
                                />
                                <ul
                                    className={listStyle}
                                    data-testid="result-list"
                                >
                                    <TrackPropertyProvider
                                        key={`track-property-provider-${propertyBeingTracked?.uprn}`}
                                        initialState={{
                                            isTracked: Boolean(
                                                propertyBeingTracked
                                                    ?.trackedProperty?.isTracked
                                            ),
                                            relationship:
                                                propertyBeingTracked
                                                    ?.trackedProperty
                                                    ?.relationship || undefined,
                                            hasPermissions: Boolean(
                                                propertyBeingTracked
                                                    ?.trackedProperty
                                                    ?.permissions
                                                    ?.hasPermissions
                                            )
                                        }}
                                    >
                                        <TrackPropertyWrapper
                                            setPropertyBeingTracked={
                                                setPropertyBeingTracked
                                            }
                                            propertyBeingTracked={
                                                propertyBeingTracked
                                            }
                                            properties={
                                                (
                                                    propertiesSearch as PropertiesSearchConnection
                                                ).edges?.map(
                                                    (e) => e?.node as Property
                                                ) as Property[]
                                            }
                                        >
                                            {({ trackClick, states }) =>
                                                properties.map(
                                                    ({ cursor, node }, idx) => {
                                                        const baths =
                                                            node?.attributes
                                                                ?.bathrooms ||
                                                            undefined;
                                                        const beds =
                                                            node?.attributes
                                                                ?.bedrooms ||
                                                            undefined;
                                                        const receptions =
                                                            node?.attributes
                                                                ?.livingRooms ||
                                                            undefined;

                                                        const badges: string[] =
                                                            [
                                                                node?.attributes
                                                                    ?.tenure ??
                                                                    '',
                                                                node?.attributes
                                                                    ?.propertyType ??
                                                                    ''
                                                            ].filter(
                                                                (x) => x !== ''
                                                            );

                                                        const attributes =
                                                            mapAttributes({
                                                                baths,
                                                                beds,
                                                                receptions
                                                            });
                                                        const localState =
                                                            states[
                                                                node?.uprn ?? ''
                                                            ];

                                                        const trackPropertyClick =
                                                            (
                                                                node: Property
                                                            ) => {
                                                                triggerTracking(
                                                                    {
                                                                        event: 'GAEvent',
                                                                        eventCategory:
                                                                            'house prices results',
                                                                        eventAction:
                                                                            'property_clickthrough',
                                                                        eventLabel:
                                                                            'property_card',
                                                                        eventValue:
                                                                            node?.uprn
                                                                                ? parseInt(
                                                                                      node?.uprn ??
                                                                                          '',
                                                                                      10
                                                                                  )
                                                                                : undefined,
                                                                        taxonomy:
                                                                            {
                                                                                postcode:
                                                                                    node
                                                                                        ?.address
                                                                                        ?.postcode,
                                                                                price_estimate:
                                                                                    node
                                                                                        ?.saleEstimate
                                                                                        ?.currentPrice,
                                                                                property_address:
                                                                                    node
                                                                                        ?.address
                                                                                        ?.fullAddress,
                                                                                num_beds:
                                                                                    node
                                                                                        ?.attributes
                                                                                        ?.bedrooms ??
                                                                                    undefined,
                                                                                uprn:
                                                                                    node?.uprn ??
                                                                                    undefined,
                                                                                num_baths:
                                                                                    node
                                                                                        ?.attributes
                                                                                        ?.bathrooms ??
                                                                                    undefined,
                                                                                listing_id:
                                                                                    node
                                                                                        ?.liveListings?.[0]
                                                                                        .listingId ??
                                                                                    undefined
                                                                            }
                                                                    }
                                                                );
                                                                trackEvent(
                                                                    'Search Result Click',
                                                                    {
                                                                        section:
                                                                            'house-prices',
                                                                        page: '/house-prices/results/',
                                                                        brand: 'zoopla',
                                                                        postcode:
                                                                            node
                                                                                ?.address
                                                                                ?.postcode,
                                                                        priceEstimate:
                                                                            node
                                                                                ?.saleEstimate
                                                                                ?.currentPrice,
                                                                        propertyAddress:
                                                                            node
                                                                                ?.address
                                                                                ?.fullAddress,
                                                                        numberBeds:
                                                                            node
                                                                                ?.attributes
                                                                                ?.bedrooms ??
                                                                            undefined,
                                                                        uprn: node?.uprn
                                                                            ? parseInt(
                                                                                  node?.uprn ??
                                                                                      '',
                                                                                  10
                                                                              )
                                                                            : undefined,
                                                                        numberBaths:
                                                                            node
                                                                                ?.attributes
                                                                                ?.bathrooms ??
                                                                            undefined,
                                                                        propertyRelationship:
                                                                            node
                                                                                ?.trackedProperty
                                                                                ?.relationship ??
                                                                            undefined,
                                                                        ...extraTaxonomy
                                                                    },
                                                                    {
                                                                        listName:
                                                                            'house prices results',
                                                                        products:
                                                                            [
                                                                                {
                                                                                    currencyCode:
                                                                                        'GBP',
                                                                                    uprn: node?.uprn
                                                                                        ? parseInt(
                                                                                              node?.uprn ??
                                                                                                  '',
                                                                                              10
                                                                                          )
                                                                                        : undefined,
                                                                                    productId:
                                                                                        node?.uprn
                                                                                            ? parseInt(
                                                                                                  node?.uprn ??
                                                                                                      '',
                                                                                                  10
                                                                                              )
                                                                                            : undefined,
                                                                                    index: idx,
                                                                                    price:
                                                                                        node
                                                                                            ?.saleEstimate
                                                                                            ?.currentPrice ??
                                                                                        0
                                                                                }
                                                                            ]
                                                                    }
                                                                );
                                                            };

                                                        const modalShowHandler =
                                                            () => {
                                                                triggerTracking(
                                                                    {
                                                                        event: 'GAEvent',
                                                                        eventCategory:
                                                                            'property consent',
                                                                        eventLabel:
                                                                            'save property',
                                                                        eventValue:
                                                                            node?.uprn,
                                                                        eventAction:
                                                                            'modal shown'
                                                                    }
                                                                );
                                                            };

                                                        return (
                                                            <li
                                                                key={`frag-${cursor}`}
                                                            >
                                                                <Card
                                                                    headingLevel="h2"
                                                                    address={
                                                                        node
                                                                            ?.address
                                                                            ?.fullAddress ||
                                                                        ''
                                                                    }
                                                                    attributes={
                                                                        attributes
                                                                    }
                                                                    url={
                                                                        node?.uprn &&
                                                                        node
                                                                            ?.address
                                                                            ?.uprn
                                                                            ? `/property/uprn/${node?.uprn}/`
                                                                            : undefined
                                                                    }
                                                                    isTracked={
                                                                        localState
                                                                            ? localState ===
                                                                              'tracked'
                                                                            : !!node
                                                                                  ?.trackedProperty
                                                                                  ?.isTracked ??
                                                                              false
                                                                    }
                                                                    hasUprn={
                                                                        !!node?.uprn &&
                                                                        !!node
                                                                            ?.address
                                                                            ?.uprn
                                                                    }
                                                                    lastSaleDate={
                                                                        node
                                                                            ?.lastSale
                                                                            ?.date
                                                                            ? new Date(
                                                                                  node?.lastSale?.date
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    badges={
                                                                        badges
                                                                    }
                                                                    lastSalePrice={
                                                                        node
                                                                            ?.lastSale
                                                                            ?.price
                                                                            ? currencyFormatter.format(
                                                                                  node
                                                                                      ?.lastSale
                                                                                      ?.price
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    estimatedPrice={buildEstimateString(
                                                                        node
                                                                            ?.saleEstimate
                                                                            ?.lowerPrice,
                                                                        node
                                                                            ?.saleEstimate
                                                                            ?.upperPrice
                                                                    )}
                                                                    ctaClickHandler={() => {
                                                                        node &&
                                                                            trackClick(
                                                                                node,
                                                                                extraTaxonomy
                                                                            );
                                                                        modalShowHandler();
                                                                    }}
                                                                    pending={
                                                                        localState ===
                                                                        'pending'
                                                                    }
                                                                    onClick={() => {
                                                                        trackPropertyClick(
                                                                            node as Property
                                                                        );
                                                                    }}
                                                                />
                                                                <InlineAdSlot
                                                                    route={
                                                                        route
                                                                    }
                                                                    listingIndex={
                                                                        idx
                                                                    }
                                                                />
                                                                <InlineAdSlot
                                                                    route={
                                                                        route
                                                                    }
                                                                    listingIndex={
                                                                        idx
                                                                    }
                                                                    isMobile
                                                                />
                                                                {idx === 7 && (
                                                                    <AdvertSlotDfp
                                                                        position={
                                                                            4
                                                                        }
                                                                        showOnly={[
                                                                            '<',
                                                                            queries
                                                                                .M[100]
                                                                        ]}
                                                                        sizes={[
                                                                            [
                                                                                300,
                                                                                16
                                                                            ],
                                                                            'fluid'
                                                                        ]}
                                                                        adUnitCode={[
                                                                            AD_UNIT_ROOTS.TEXT_LINK,
                                                                            AD_UNIT_SECTION.HOUSE_PRICES,
                                                                            getPageSectionForAdUnit(
                                                                                route
                                                                            )
                                                                        ]}
                                                                        adbridgClass={
                                                                            ADBRIDG_CLASS.INLINE_SPECIAL
                                                                        }
                                                                        adbridgDataClass={
                                                                            ADBRIDG_CLASS.ADB_TEXT_LINK
                                                                        }
                                                                    />
                                                                )}
                                                            </li>
                                                        );
                                                    }
                                                )
                                            }
                                        </TrackPropertyWrapper>
                                    </TrackPropertyProvider>
                                </ul>

                                <Box pt="S500">
                                    <Pagination
                                        totalPages={Math.ceil(
                                            totalResults / PAGE_SIZE
                                        )}
                                        currentPage={currentPage}
                                        pageNumberToURL={pageNumberToUrl}
                                    />
                                </Box>
                            </>
                        ) : (
                            <InfoPanel
                                type="alert"
                                strapline="No results found"
                                title="Sorry, we cannot find any results for your search."
                                text="You can change your search filters or start again."
                            />
                        )}

                        <p
                            className={`${disclaimerTextStyle} ${fontGroups.knife}`}
                        >
                            We get our sold house price data from HM Land
                            Registry/Registers of Scotland. It can take up to 3
                            months for it to appear here.
                        </p>
                    </div>
                </Box>
            </Layout>
        </>
    );
};

const HousePricesResultsPageWithApollo = graphQLWrapper(HousePricesResultsPage);

export default HousePricesResultsPageWithApollo;
